/**
 * ErrorBoundary — fängt React-Render-Fehler in einem Sub-Tree.
 *
 * Hintergrund: Ein einzelner Fehler in einer Sub-Komponente (z.B. undefined
 * Variable, unerwartetes Response-Schema) wirft sonst die GANZE Page raus
 * — White-Screen statt nur eines kaputten Tabs.
 *
 * Nutzung:
 *   <ErrorBoundary name="SecuritySection">
 *     <SecuritySection />
 *   </ErrorBoundary>
 *
 * Mit Custom-Fallback:
 *   <ErrorBoundary name="ChartView" fallback={<MyCustomFallback />}>
 *     <ChartView />
 *   </ErrorBoundary>
 *
 * Pro Tab/Section eigene Boundary verwenden, damit ein Crash dort den
 * Rest der Page nicht mit nimmt.
 */
import { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

import { authFetch } from '../utils/authFetch';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const name = this.props.name || 'Unknown';
    // Konsolen-Log mit Boundary-Namen damit man im Production-Build den
    // Crash-Ort schnell findet (minified-Stack ist sonst unbrauchbar).
    console.error(
      `[ErrorBoundary:${name}] Render error caught:`,
      error,
      errorInfo
    );
    this.setState({ errorInfo });

    // An die Backend-Telemetrie schicken (best-effort, kein await). Über
    // `authFetch`: Ist jemand angemeldet, verlangt die CSRF-Prüfung den
    // X-CSRF-Token-Header — ohne ihn kam seit 18.09.2026 keine Meldung mehr an
    // (403), und ein Absturz auf Prod liess sich nur noch raten.
    if (typeof window !== 'undefined' && window.fetch) {
      authFetch('/api/telemetry/frontend-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          boundary: name,
          message: error?.message || String(error),
          stack: error?.stack || null,
          component_stack: errorInfo?.componentStack || null,
          path: window.location?.pathname || null,
        }),
        keepalive: true,
      })
        .then((antwort) => {
          if (!antwort.ok) {
            console.error(`[ErrorBoundary:${name}] Fehlermeldung an den Server abgelehnt: HTTP ${antwort.status}`);
          }
        })
        .catch((meldeFehler) => {
          console.error(`[ErrorBoundary:${name}] Fehlermeldung an den Server nicht gesendet:`, meldeFehler);
        });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Custom Fallback vom Aufrufer
    if (this.props.fallback) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback({
            error: this.state.error,
            reset: this.handleReset,
          })
        : this.props.fallback;
    }

    const name = this.props.name || '';
    const compact = this.props.compact === true;
    const t = this.props.t; // optional — Boundary funktioniert auch ohne i18n

    const tr = (key, fallback) => (t ? t(key, fallback) : fallback);

    // `rahmen`: legt einen Rahmen um die Meldung — die letzte Fehlergrenze
    // (`SeitenGrenze` um den ganzen Routen-Block) stellt damit den
    // Legal-Footer dazu, denn wenn SIE greift, ist das Layout mit weg.
    const rahmen = this.props.rahmen || (meldung => meldung);

    return rahmen(
      <div
        role="alert"
        style={{
          padding: compact ? '12px 16px' : '24px',
          margin: compact ? '8px 0' : '16px 0',
          background: 'color-mix(in srgb, var(--danger) 6%, transparent)',
          border: '1px solid color-mix(in srgb, var(--danger) 25%, transparent)',
          borderRadius: 'var(--radius)',
          color: 'var(--text-primary)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
          }}
        >
          <AlertTriangle
            size={compact ? 18 : 22}
            style={{ color: 'var(--danger)', flexShrink: 0, marginTop: '2px' }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                marginBottom: '4px',
              }}
            >
              {tr(
                'error_boundary.title',
                'Dieser Bereich konnte nicht geladen werden'
              )}
            </div>
            <div
              style={{
                fontSize: '1rem',
                color: 'var(--text-secondary)',
                marginBottom: '12px',
              }}
            >
              {tr(
                'error_boundary.description',
                'Ein unerwarteter Fehler ist aufgetreten. Der Rest der Seite funktioniert weiter.'
              )}
              {name && (
                <span
                  style={{
                    marginLeft: '6px',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '1rem',
                    color: 'var(--text-tertiary)',
                  }}
                >
                  [{name}]
                </span>
              )}
            </div>
            {this.state.error?.message && !compact && (
              <details
                style={{
                  fontSize: '1rem',
                  fontFamily: 'var(--font-mono)',
                  color: 'var(--text-tertiary)',
                  marginBottom: '12px',
                }}
              >
                <summary style={{ cursor: 'pointer' }}>
                  {tr('error_boundary.details', 'Technische Details')}
                </summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    marginTop: '6px',
                  }}
                >
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={this.handleReset}
                className="btn btn-secondary btn-sm"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <RefreshCw size={14} />
                {tr('error_boundary.retry', 'Erneut versuchen')}
              </button>
              {!compact && (
                <button
                  type="button"
                  onClick={this.handleReload}
                  className="btn btn-ghost btn-sm"
                >
                  {tr('error_boundary.reload_page', 'Seite neu laden')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
